import { getBaseBoostedStrategyHref } from "~/lib/env";
import { m } from "~/lib/i18n";
import {
  formatLanguageDate,
  formatLanguageTime,
} from "~/lib/language-lens/intl";

export type CampaignStatus = "active" | "ended" | "upcoming";
export type CampaignListType = "ordered" | "unordered";
export type CampaignHeroVariant =
  | "arbitrum"
  | "arbitrum-giveaway"
  | "base"
  | "coinbase-assets"
  | "eth"
  | "mag7"
  | "mag7-spacex"
  | "solana"
  | "stocks"
  | "trading-competition";

interface CampaignDefinitionBase {
  bodyNote?: string;
  createdAtMs: number;
  cta?: {
    href: string;
    label: string;
  };
  dateLabel: string;
  details: string[];
  detailsListType?: CampaignListType;
  detailsTitle?: string;
  finePrint: string[];
  heroVariant: CampaignHeroVariant;
  id: string;
  startsAtMs?: number;
  summary: string;
  title: string;
}

type StaticCampaignDefinition = CampaignDefinitionBase & {
  activeUntilMs?: never;
  status: CampaignStatus;
};

type TimeBoundCampaignDefinition = CampaignDefinitionBase & {
  activeUntilMs: number;
  status?: never;
};

export type CampaignDefinition =
  | StaticCampaignDefinition
  | TimeBoundCampaignDefinition;

export const ETH_L1_CAMPAIGN_ID = "eth-l1-double-points";
export const ARBITRUM_CAMPAIGN_ID = "arbitrum-double-points";
export const ARBITRUM_GIVEAWAY_CAMPAIGN_ID = "arbitrum-giveaway-1000";
export const STOCKS_CAMPAIGN_ID = "stocks-4x-points";
export const SOLANA_CAMPAIGN_ID = "solana-2x-points";
export const MAG7_PORTFOLIO_CAMPAIGN_ID = "mag7-portfolio-rewards";
export const MAG7_SPACEX_PORTFOLIO_CAMPAIGN_ID =
  "mag7-spacex-portfolio-rewards";
export const TRADING_COMPETITION_CAMPAIGN_ID = "trading-competition-500";
export const BASE_BOOST_ROUND_2_CAMPAIGN_ID = "base-portfolio-boost-round-2";
export const COINBASE_ASSETS_BOOSTED_PORTFOLIO_CAMPAIGN_ID =
  "coinbase-assets-boosted-portfolio";

const BASE_BOOSTED_STRATEGY_ID = "01KJT32V6CJNR7AQXAJBJ54DDG";
export const BASE_BOOSTED_CAMPAIGN_HREF =
  getBaseBoostedStrategyHref() ??
  `/strategy/${encodeURIComponent(BASE_BOOSTED_STRATEGY_ID)}`;
const ARBITRUM_STRATEGY_ID = "01KNMSK4V78SF5QZMSZB68SV9E";
export const ARBITRUM_STRATEGY_HREF = `/strategy/${encodeURIComponent(
  ARBITRUM_STRATEGY_ID,
)}`;
const COINBASE_ASSETS_BOOSTED_PORTFOLIO_STRATEGY_ID =
  "01KQX23487957330BYJHPVG1KP";
const COINBASE_ASSETS_BOOSTED_PORTFOLIO_HREF = `/strategy/${encodeURIComponent(
  COINBASE_ASSETS_BOOSTED_PORTFOLIO_STRATEGY_ID,
)}`;
const SOLANA_ECOSYSTEM_PORTFOLIO_STRATEGY_ID = "01KR1J4BPFJR121JMJ7RK0Y3E7";
export const SOLANA_ECOSYSTEM_PORTFOLIO_HREF = `/strategy/${encodeURIComponent(
  SOLANA_ECOSYSTEM_PORTFOLIO_STRATEGY_ID,
)}`;
export const MAG7_STAGING_PORTFOLIO_STRATEGY_ID = "01KBMW1WB6P33JN3AE6W8A3EPK";
export const MAG7_PRODUCTION_PORTFOLIO_STRATEGY_ID =
  "01KV68M2Y685X59DWAEVX5D5X3";

const MAG7_PRODUCTION_HOSTNAMES = new Set([
  "app.glider.fi",
  "glider.fi",
  "webapp-v2.glidercloud.dev",
  "www.glider.fi",
]);

export function getMag7PortfolioStrategyHref(
  hostname: string | undefined = typeof window === "undefined"
    ? undefined
    : window.location.hostname,
): string {
  const normalizedHostname = hostname?.trim().toLowerCase() ?? "";
  const strategyId = MAG7_PRODUCTION_HOSTNAMES.has(normalizedHostname)
    ? MAG7_PRODUCTION_PORTFOLIO_STRATEGY_ID
    : MAG7_STAGING_PORTFOLIO_STRATEGY_ID;

  return `/strategy/${encodeURIComponent(strategyId)}`;
}

export const MAG7_PORTFOLIO_HREF = getMag7PortfolioStrategyHref();

const PAST_REWARD_RETENTION_MS = 60 * 24 * 60 * 60 * 1000;

const MARCH_2026_ETH_L1_CAMPAIGN_CREATED_AT_MS = Date.UTC(2026, 2, 6, 0, 0, 0);
const MARCH_2026_ETH_L1_CAMPAIGN_END_MS = Date.UTC(2026, 2, 31, 8, 0, 0);
const MARCH_2026_BASE_BOOST_CREATED_AT_MS = Date.UTC(2026, 2, 11, 0, 0, 0);
const APRIL_2026_BASE_BOOST_END_MS = Date.UTC(2026, 3, 9, 8, 0, 0);
const APRIL_2026_ARBITRUM_CAMPAIGN_CREATED_AT_MS = Date.UTC(
  2026,
  3,
  3,
  0,
  0,
  0,
);
export const ARBITRUM_CAMPAIGN_START_MS = Date.UTC(2026, 3, 8, 0, 0, 0);
export const ARBITRUM_CAMPAIGN_END_MS = Date.UTC(2026, 5, 1, 8, 0, 0);
const APRIL_2026_BASE_BOOST_ROUND_2_CREATED_AT_MS = Date.UTC(
  2026,
  3,
  7,
  0,
  0,
  0,
);
export const BASE_BOOST_ROUND_2_START_MS = Date.UTC(2026, 3, 7, 0, 0, 0);
const MAY_2026_BASE_BOOST_END_MS = Date.UTC(2026, 4, 9, 8, 0, 0);
const APRIL_2026_ARB_GIVEAWAY_CREATED_AT_MS = Date.UTC(2026, 3, 20, 0, 0, 0);
const APRIL_2026_ARB_GIVEAWAY_END_MS = Date.UTC(2026, 3, 27, 4, 0, 0);
const MARCH_2026_STOCKS_CAMPAIGN_CREATED_AT_MS = Date.UTC(2026, 2, 20, 0, 0, 0);
const MARCH_2026_TRADING_COMP_CREATED_AT_MS = Date.UTC(2026, 2, 24, 0, 0, 0);
const MARCH_2026_TRADING_COMP_END_MS = Date.UTC(2026, 3, 7, 8, 0, 0);
const MAY_2026_COINBASE_ASSETS_CAMPAIGN_CREATED_AT_MS = Date.UTC(
  2026,
  4,
  8,
  0,
  0,
  0,
);
const JUNE_2026_COINBASE_ASSETS_CAMPAIGN_END_MS = Date.UTC(2026, 5, 7, 0, 0, 0);
export const SOLANA_CAMPAIGN_START_MS = Date.UTC(2026, 4, 12, 0, 0, 0);
export const SOLANA_CAMPAIGN_END_MS = Date.UTC(2026, 5, 11, 8, 0, 0);
export const MAG7_PORTFOLIO_CAMPAIGN_START_MS = Date.UTC(2026, 5, 17, 8, 0, 0);
export const MAG7_PORTFOLIO_CAMPAIGN_END_MS = Date.UTC(2026, 8, 17, 4, 0, 0);
// Jul 30 through Sep 28, 2026, both at 09:30 ET - the window from webapp v1's
// Strats by Bitwise campaign (launch postponed there; v2 is not
// customer-facing, so the campaign shows ungated for preview).
export const MAG7_SPACEX_PORTFOLIO_CAMPAIGN_START_MS = Date.UTC(
  2026,
  6,
  30,
  13,
  30,
  0,
);
export const MAG7_SPACEX_PORTFOLIO_CAMPAIGN_END_MS = Date.UTC(
  2026,
  8,
  28,
  13,
  30,
  0,
);
export const MAG7_SPACEX_STAGING_PORTFOLIO_STRATEGY_ID =
  "01KYHP1SJXEF46V8GH3P00955Y";
// TODO: point at the production Mag7X strategy once it exists (webapp v1's
// MAG7_SPACEX_PRODUCTION_PORTFOLIO_STRATEGY_ID is still unset too).
export const MAG7_SPACEX_PORTFOLIO_HREF = `/strategy/${encodeURIComponent(
  MAG7_SPACEX_STAGING_PORTFOLIO_STRATEGY_ID,
)}`;

function createMag7SpacexCampaign(): CampaignDefinition {
  const startDate = formatLanguageDate(
    MAG7_SPACEX_PORTFOLIO_CAMPAIGN_START_MS,
    {
      day: "numeric",
      month: "long",
      timeZone: "UTC",
    },
  );
  const endDate = formatLanguageDate(MAG7_SPACEX_PORTFOLIO_CAMPAIGN_END_MS, {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
  const startDateLong = formatLanguageDate(
    MAG7_SPACEX_PORTFOLIO_CAMPAIGN_START_MS,
    {
      day: "numeric",
      month: "long",
      timeZone: "UTC",
      year: "numeric",
    },
  );
  const startTime = formatLanguageTime(
    MAG7_SPACEX_PORTFOLIO_CAMPAIGN_START_MS,
    {
      hour: "2-digit",
      hour12: false,
      minute: "2-digit",
      timeZone: "UTC",
      timeZoneName: "short",
    },
  );

  return {
    id: MAG7_SPACEX_PORTFOLIO_CAMPAIGN_ID,
    title: m.campaigns_mag7_spacex_title(),
    createdAtMs: MAG7_SPACEX_PORTFOLIO_CAMPAIGN_START_MS,
    startsAtMs: MAG7_SPACEX_PORTFOLIO_CAMPAIGN_START_MS,
    dateLabel: m.campaigns_mag7_spacex_date_label({ endDate, startDate }),
    summary: m.campaigns_mag7_spacex_summary(),
    activeUntilMs: MAG7_SPACEX_PORTFOLIO_CAMPAIGN_END_MS,
    heroVariant: "mag7-spacex",
    detailsListType: "ordered",
    detailsTitle: m.campaigns_mag7_spacex_details_title(),
    details: [
      m.campaigns_mag7_spacex_detail_open(),
      m.campaigns_mag7_spacex_detail_deposit(),
      m.campaigns_mag7_spacex_detail_manage(),
      m.campaigns_mag7_spacex_detail_apr(),
    ],
    finePrint: [
      m.campaigns_mag7_spacex_fine_print_holdings(),
      m.campaigns_mag7_spacex_fine_print_managed(),
      m.campaigns_mag7_spacex_fine_print_tokenized(),
      m.campaigns_mag7_spacex_fine_print_custody(),
      m.campaigns_mag7_spacex_fine_print_minimum(),
      m.campaigns_mag7_spacex_fine_print_balance_cap(),
      m.campaigns_mag7_spacex_fine_print_dates({
        endDate,
        startDate: startDateLong,
        startTime,
      }),
      m.campaigns_mag7_spacex_fine_print_eligibility(),
      m.campaigns_mag7_spacex_fine_print_withdrawals(),
    ],
    cta: {
      href: MAG7_SPACEX_PORTFOLIO_HREF,
      label: m.campaigns_mag7_spacex_cta_label(),
    },
  };
}

function createMag7Campaign(): CampaignDefinition {
  const startDate = formatLanguageDate(MAG7_PORTFOLIO_CAMPAIGN_START_MS, {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
  const endDate = formatLanguageDate(MAG7_PORTFOLIO_CAMPAIGN_END_MS, {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
  const startDateLong = formatLanguageDate(MAG7_PORTFOLIO_CAMPAIGN_START_MS, {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
    year: "numeric",
  });
  const startTime = formatLanguageTime(MAG7_PORTFOLIO_CAMPAIGN_START_MS, {
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  });

  return {
    id: MAG7_PORTFOLIO_CAMPAIGN_ID,
    title: m.campaigns_mag7_title(),
    createdAtMs: MAG7_PORTFOLIO_CAMPAIGN_START_MS,
    startsAtMs: MAG7_PORTFOLIO_CAMPAIGN_START_MS,
    dateLabel: m.campaigns_mag7_date_label({ endDate, startDate }),
    summary: m.campaigns_mag7_summary(),
    activeUntilMs: MAG7_PORTFOLIO_CAMPAIGN_END_MS,
    heroVariant: "mag7",
    detailsListType: "ordered",
    detailsTitle: m.campaigns_mag7_details_title(),
    details: [
      m.campaigns_mag7_detail_open(),
      m.campaigns_mag7_detail_deposit(),
      m.campaigns_mag7_detail_rewards(),
    ],
    finePrint: [
      m.campaigns_mag7_fine_print_holdings(),
      m.campaigns_mag7_fine_print_minimum(),
      m.campaigns_mag7_fine_print_balance_cap(),
      m.campaigns_mag7_fine_print_apr(),
      m.campaigns_mag7_fine_print_dates({
        endDate,
        startDate: startDateLong,
        startTime,
      }),
      m.campaigns_mag7_fine_print_withdrawals(),
      m.campaigns_mag7_fine_print_eligibility(),
    ],
    cta: {
      href: MAG7_PORTFOLIO_HREF,
      label: m.campaigns_mag7_cta_label(),
    },
  };
}

function isTimeBoundCampaign(
  campaign: CampaignDefinition,
): campaign is TimeBoundCampaignDefinition {
  return typeof campaign.activeUntilMs === "number";
}

export function getCampaignStatus(
  campaign: CampaignDefinition,
  nowMs: number = Date.now(),
): CampaignStatus {
  if (typeof campaign.startsAtMs === "number" && nowMs < campaign.startsAtMs) {
    return "upcoming";
  }

  if (isTimeBoundCampaign(campaign)) {
    return nowMs > campaign.activeUntilMs ? "ended" : "active";
  }

  return campaign.status;
}

export function getActiveCampaignCta(
  campaign: CampaignDefinition,
  nowMs: number = Date.now(),
): CampaignDefinition["cta"] {
  return getCampaignStatus(campaign, nowMs) === "active"
    ? campaign.cta
    : undefined;
}

function shouldShowCampaign(
  campaign: CampaignDefinition,
  nowMs: number = Date.now(),
): boolean {
  const status = getCampaignStatus(campaign, nowMs);

  if (status === "upcoming") {
    return false;
  }

  if (status !== "ended" || !isTimeBoundCampaign(campaign)) {
    return true;
  }

  return nowMs <= campaign.activeUntilMs + PAST_REWARD_RETENTION_MS;
}

export const CAMPAIGNS: readonly CampaignDefinition[] = [
  {
    id: SOLANA_CAMPAIGN_ID,
    title: "Earn 2x Points on Solana Assets",
    createdAtMs: SOLANA_CAMPAIGN_START_MS,
    startsAtMs: SOLANA_CAMPAIGN_START_MS,
    dateLabel: "May 12-June 11",
    summary:
      "Solana ecosystem tokens earn 2x points for 30 days when held directly on Glider.",
    activeUntilMs: SOLANA_CAMPAIGN_END_MS,
    heroVariant: "solana",
    details: [
      "Hold JUP, JTO, KMNO, PYTH, PUMP, RAY, or CLOUD directly in your Glider investing account or the Solana Ecosystem Portfolio.",
      "Earn 2x points for every dollar held through the daily snapshot during the campaign window.",
      "Keep qualifying balances in place until the daily snapshot to count for that day.",
    ],
    finePrint: [
      "Campaign runs from May 12 to June 11.",
      "Only JUP, JTO, KMNO, PYTH, PUMP, RAY, and CLOUD qualify.",
      "Balances in other portfolios do not qualify.",
      "Tokens sourced or held outside Glider do not qualify.",
    ],
    cta: {
      href: SOLANA_ECOSYSTEM_PORTFOLIO_HREF,
      label: "Use this Strategy",
    },
  },
  {
    id: COINBASE_ASSETS_BOOSTED_PORTFOLIO_CAMPAIGN_ID,
    title: "Earn up to 20% APR with the Coinbase Assets Boosted Portfolio",
    createdAtMs: MAY_2026_COINBASE_ASSETS_CAMPAIGN_CREATED_AT_MS,
    startsAtMs: MAY_2026_COINBASE_ASSETS_CAMPAIGN_CREATED_AT_MS,
    dateLabel: "May 8-June 7",
    summary:
      "Hold the Coinbase Assets Boosted Portfolio on Glider and earn up to 20% APR on your balance during the reward window.",
    activeUntilMs: JUNE_2026_COINBASE_ASSETS_CAMPAIGN_END_MS,
    heroVariant: "coinbase-assets",
    detailsListType: "ordered",
    detailsTitle: "Participation steps",
    details: [
      "Open the Coinbase Assets Boosted Portfolio on Glider.",
      "Deposit at least $100 in the portfolio.",
      "Sit back and let rewards accrue.",
    ],
    finePrint: [
      "The portfolio holds cbBTC, cbETH, cbXRP, cbMEGA, and SOL on Base, auto-rebalancing hourly.",
      "A minimum deposit of $100 is required to be eligible for rewards.",
      "Rewards are paid on balances up to $30,000 per user.",
      "Reward APR is capped at 20% and may fall below depending on total balances on the platform.",
      "Rewards run continuously from May 8 through June 7.",
      "Rewards are only distributed to users of the portfolio linked above, and not other portfolios.",
      "Withdrawals are allowed at any time. There are no lockups.",
    ],
    cta: {
      href: COINBASE_ASSETS_BOOSTED_PORTFOLIO_HREF,
      label: "Use this Strategy",
    },
  },
  {
    id: ARBITRUM_GIVEAWAY_CAMPAIGN_ID,
    title: "Win $1,000 with Arbitrum on Glider",
    createdAtMs: APRIL_2026_ARB_GIVEAWAY_CREATED_AT_MS,
    startsAtMs: APRIL_2026_ARB_GIVEAWAY_CREATED_AT_MS,
    dateLabel: "April 20-26",
    summary:
      "Perform one action on Arbitrum this week and you're entered to win $1,000. One entry per wallet.",
    activeUntilMs: APRIL_2026_ARB_GIVEAWAY_END_MS,
    heroVariant: "arbitrum-giveaway",
    detailsTitle: "How it works",
    details: [
      "Deposit, bridge USDC to Arbitrum, buy, sell, or create and copy a portfolio on Glider using Arbitrum to enter.",
      "One entry per wallet.",
      "One winner takes $1,000.",
    ],
    finePrint: [
      "Reward runs Monday April 20 to Sunday April 26 at midnight ET.",
      "Winner announced the following week.",
      "To be publicly announced as the winner, link your X account in Glider settings. If no X account is linked, we will be unable to make a public announcement.",
    ],
  },
  {
    id: ARBITRUM_CAMPAIGN_ID,
    title: "Earn 2x Points on Arbitrum Assets",
    createdAtMs: APRIL_2026_ARBITRUM_CAMPAIGN_CREATED_AT_MS,
    startsAtMs: ARBITRUM_CAMPAIGN_START_MS,
    dateLabel: "April 8-June 1",
    summary:
      "Arbitrum is now live on Glider. Every Arbitrum asset you hold earns 2x points compared to any other chain on the platform through June 1.",
    activeUntilMs: ARBITRUM_CAMPAIGN_END_MS,
    heroVariant: "arbitrum",
    details: [
      "Deposit any Arbitrum asset into Glider.",
      "Earn 2x points per dollar held each day compared to any other chain.",
      "Points accumulate automatically.",
    ],
    finePrint: [
      "Arbitrum assets earn 2x points compared to all other chains on Glider throughout the reward period.",
      "Reward runs from April 8 to June 1 at 8:00 AM UTC.",
      "Balances must remain in your portfolio until the daily snapshot to count.",
      "All Arbitrum assets held directly on Glider qualify.",
    ],
    cta: {
      href: ARBITRUM_STRATEGY_HREF,
      label: "Use this Strategy",
    },
  },
  {
    id: TRADING_COMPETITION_CAMPAIGN_ID,
    title: "$500 Trading Competition",
    createdAtMs: MARCH_2026_TRADING_COMP_CREATED_AT_MS,
    dateLabel: "March 24-April 7",
    summary:
      "We're giving away $500 to one winner. Set up recurring buys on any onchain asset and you're automatically entered. The more you set up, the better your chances.",
    activeUntilMs: MARCH_2026_TRADING_COMP_END_MS,
    heroVariant: "trading-competition",
    detailsListType: "ordered",
    details: [
      "Set up a recurring buy on any onchain asset on Glider.",
      "Each recurring order must be a minimum of $25.",
      "Complete at least 4 recurring buys between March 24 and April 7.",
      "That's it. We track everything automatically, nothing to submit.",
    ],
    finePrint: [
      "One winner takes the full $500.",
      "Minimum recurring order size of $25 per trade qualifies.",
      "Competition runs March 24 to April 7 only.",
      "Only recurring buys on Glider qualify.",
    ],
  },
  {
    id: STOCKS_CAMPAIGN_ID,
    title: "Earn 2x points on Stocks",
    createdAtMs: MARCH_2026_STOCKS_CAMPAIGN_CREATED_AT_MS,
    dateLabel: "March 23-April 24",
    summary:
      "Onchain stocks are now live on Glider, powered by Ondo Finance. To celebrate, earn 4x points on all onchain stock holdings from March 23 to March 31. After that, earn 2x points until April 24.",
    activeUntilMs: Date.UTC(2026, 3, 24, 8, 0, 0),
    heroVariant: "stocks",
    details: [
      "Qualifying onchain stock balances earn 4x points for every dollar held in Glider each day from March 23 to March 31.",
      "From April 1 to April 24, qualifying balances earn 2x points per dollar held each day.",
      "Balances must remain in the portfolio until the daily snapshot to count.",
    ],
    finePrint: ["Only tokenized stocks powered by Ondo Finance qualify."],
    cta: {
      href: "/stocks",
      label: "Explore Stocks",
    },
  },
  {
    id: ETH_L1_CAMPAIGN_ID,
    title: "Earn 2x points on ETH L1 Assets",
    createdAtMs: MARCH_2026_ETH_L1_CAMPAIGN_CREATED_AT_MS,
    dateLabel: "March 6-31",
    summary:
      "ETH L1 is now live on Glider. All ETH L1 assets earn 2x points during the reward window. That's 2 points for every dollar held in Glider each day.",
    activeUntilMs: MARCH_2026_ETH_L1_CAMPAIGN_END_MS,
    heroVariant: "eth",
    bodyNote:
      "Time to deposit those assets that have been collecting dust in your wallet.",
    details: [
      "Qualifying ETH L1 balances earn 2 points for every dollar held in Glider each day during the reward window.",
      "Balances must remain in the portfolio until the daily snapshot to count.",
    ],
    finePrint: [
      "Wrapped ETH variants count when held directly.",
      "LP positions, synthetic assets, and manual OTC transfers do not count.",
    ],
  },
  {
    id: "base-portfolio-boost",
    title: "Earn up to 25% APR with the Base Boosted Portfolio",
    createdAtMs: MARCH_2026_BASE_BOOST_CREATED_AT_MS,
    dateLabel: "March 11-April 9",
    summary:
      "Starting March 11, earn up to 25% APR on your balance in the Base Boosted Portfolio.",
    activeUntilMs: APRIL_2026_BASE_BOOST_END_MS,
    heroVariant: "base",
    detailsListType: "ordered",
    detailsTitle: "To participate",
    bodyNote:
      "Rewards will automatically accrue to your balance. No claims needed.",
    details: [
      'Click "Use this Strategy" on the Base Boost strategy page.',
      "Deposit at least $100.",
      "Sit back and Glide.",
    ],
    finePrint: [
      "Balances must remain at or above $100 to qualify.",
      "Rewards are paid on balances up to $30,000.",
      "Reward APR may fall below 25% depending on the total balances received on the platform.",
      "Rewards run for 30 days continuously, beginning March 11.",
      "Rewards are only distributed to users of the linked Base Boost portfolio.",
    ],
    cta: {
      href: BASE_BOOSTED_CAMPAIGN_HREF,
      label: "Use this Strategy",
    },
  },
  {
    id: BASE_BOOST_ROUND_2_CAMPAIGN_ID,
    title: "Earn up to 25% APR with the Base Boosted Portfolio (Round 2)",
    createdAtMs: APRIL_2026_BASE_BOOST_ROUND_2_CREATED_AT_MS,
    startsAtMs: BASE_BOOST_ROUND_2_START_MS,
    dateLabel: "April 7-May 9",
    summary:
      "Keep your rewards going with the Base Boosted Portfolio and earn up to 25% APR on your balance through May 9.",
    activeUntilMs: MAY_2026_BASE_BOOST_END_MS,
    heroVariant: "base",
    detailsListType: "ordered",
    detailsTitle: "To participate",
    bodyNote:
      "Rewards will automatically accrue to your balance. No claims needed.",
    details: [
      "Open the Base Boosted Portfolio on Glider.",
      "Deposit at least $150 in Base assets starting April 7.",
      "Sit back and let rewards accrue.",
    ],
    finePrint: [
      "All users, including existing participants, must make one or more new deposits totaling at least $150 in Base assets starting April 7 to be eligible.",
      "Rewards are paid on balances up to $30,000.",
      "Reward APR may fall below 25% depending on total balances on the platform.",
      "Rewards run continuously until May 9.",
      "Rewards are only distributed to users of the portfolio linked above.",
    ],
    cta: {
      href: BASE_BOOSTED_CAMPAIGN_HREF,
      label: "Use this Strategy",
    },
  },
] as const;

export function getCampaigns(nowMs: number = Date.now()): CampaignDefinition[] {
  return [
    createMag7SpacexCampaign(),
    createMag7Campaign(),
    ...CAMPAIGNS,
  ].filter((campaign) =>
    shouldShowCampaign(campaign, nowMs),
  );
}

export function sortCampaignsByRecency(
  campaigns: readonly CampaignDefinition[],
): CampaignDefinition[] {
  return [...campaigns].sort(
    (left, right) => right.createdAtMs - left.createdAtMs,
  );
}

export function splitCampaignsByStatus(
  campaigns: readonly CampaignDefinition[],
  nowMs: number = Date.now(),
): {
  active: CampaignDefinition[];
  ended: CampaignDefinition[];
} {
  const active: CampaignDefinition[] = [];
  const ended: CampaignDefinition[] = [];

  for (const campaign of campaigns) {
    const status = getCampaignStatus(campaign, nowMs);

    if (status === "active") {
      active.push(campaign);
      continue;
    }

    if (status === "ended") {
      ended.push(campaign);
    }
  }

  return {
    active: sortCampaignsByRecency(active),
    ended: sortCampaignsByRecency(ended),
  };
}

export function findCampaignById(
  campaigns: readonly CampaignDefinition[],
  campaignId: string | null | undefined,
): CampaignDefinition | null {
  if (!campaignId) {
    return null;
  }

  return campaigns.find((campaign) => campaign.id === campaignId) ?? null;
}
